// API Client for Sumind Assistant Backend

import axios, { AxiosInstance, AxiosError } from 'axios';
import {
  User,
  AuthTokens,
  LoginRequest,
  ConversationSession,
  AgentPrompt,
  PromptUpdate,
  DashboardStats,
  SystemHealth,
  AdvancedAnalytics,
  ConversationSummary,
  UserManagement,
  ApiError,
} from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor - add auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('access_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor - handle errors
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        if (error.response?.status === 401) {
          // Token expired, try to refresh
          try {
            await this.refreshToken();
            // Retry original request
            return this.client.request(error.config!);
          } catch (refreshError) {
            // Refresh failed, logout
            this.clearTokens();
            window.location.href = '/login';
          }
        }
        return Promise.reject(this.handleError(error));
      }
    );
  }

  private handleError(error: AxiosError): ApiError {
    if (error.response) {
      return {
        detail: (error.response.data as any)?.detail || error.message,
        status_code: error.response.status,
      };
    }
    return {
      detail: error.message || 'Network error',
      status_code: 0,
    };
  }

  // ========== AUTH ENDPOINTS ==========

  async login(credentials: LoginRequest): Promise<AuthTokens> {
    const formData = new URLSearchParams();
    formData.append('username', credentials.email);
    formData.append('password', credentials.password);

    const response = await this.client.post<AuthTokens>('/auth/login', formData, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });

    // Store tokens
    localStorage.setItem('access_token', response.data.access_token);
    localStorage.setItem('refresh_token', response.data.refresh_token);

    return response.data;
  }

  async getCurrentUser(): Promise<User> {
    const response = await this.client.get<User>('/auth/me');
    return response.data;
  }

  async refreshToken(): Promise<AuthTokens> {
    const refresh_token = localStorage.getItem('refresh_token');
    if (!refresh_token) {
      throw new Error('No refresh token');
    }

    const response = await this.client.post<AuthTokens>('/auth/refresh', {
      refresh_token,
    });

    localStorage.setItem('access_token', response.data.access_token);
    return response.data;
  }

  async logout(): Promise<void> {
    try {
      // Call backend to invalidate refresh tokens
      await this.client.post('/auth/logout');
    } catch (error) {
      // Even if backend call fails, clear local tokens
      console.error('Backend logout failed:', error);
    } finally {
      this.clearTokens();
    }
  }

  private clearTokens(): void {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  }

  // ========== CHAT ENDPOINTS ==========

  async createSession(channel: string = 'webchat'): Promise<ConversationSession> {
    const response = await this.client.post<ConversationSession>('/conversations', {
      channel,
      channel_user_id: this.getOrCreateDeviceId(),
    });
    return response.data;
  }

  async sendMessage(sessionId: string, content: string): Promise<ConversationSession> {
    const response = await this.client.post<ConversationSession>(
      `/conversations/${sessionId}/messages`,
      {
        content,
        metadata: {
          source: 'webchat',
          timestamp: Date.now(),
        },
      }
    );
    return response.data;
  }

  async getSession(sessionId: string): Promise<ConversationSession> {
    const response = await this.client.get<ConversationSession>(`/conversations/${sessionId}`);
    return response.data;
  }

  async listSessions(limit: number = 20): Promise<ConversationSession[]> {
    const response = await this.client.get<ConversationSession[]>('/conversations', {
      params: { limit },
    });
    return response.data;
  }

  async deleteSession(sessionId: string): Promise<void> {
    await this.client.delete(`/conversations/${sessionId}`);
  }

  // ========== ADMIN ENDPOINTS ==========

  async getDashboardStats(): Promise<DashboardStats> {
    const response = await this.client.get<DashboardStats>('/admin/dashboard');
    return response.data;
  }

  async getSystemHealth(): Promise<SystemHealth> {
    const response = await this.client.get<SystemHealth>('/admin/health');
    return response.data;
  }

  async getAllConversations(
    statusFilter?: string,
    limit: number = 50
  ): Promise<ConversationSummary[]> {
    const response = await this.client.get<ConversationSummary[]>('/admin/conversations', {
      params: { status_filter: statusFilter, limit },
    });
    return response.data;
  }

  async getAdvancedAnalytics(
    timeRange: '24h' | '7d' | '30d' | '90d' = '7d',
    limit: number = 500
  ): Promise<AdvancedAnalytics> {
    const response = await this.client.get<AdvancedAnalytics>('/admin/analytics/advanced', {
      params: { time_range: timeRange, limit },
    });
    return response.data;
  }

  async getAllUsers(limit: number = 100): Promise<UserManagement[]> {
    const response = await this.client.get<UserManagement[]>('/admin/users', {
      params: { limit },
    });
    return response.data;
  }

  async toggleUserActive(userId: string): Promise<{ message: string; user_id: string; is_active: boolean }> {
    const response = await this.client.patch(`/admin/users/${userId}/toggle-active`);
    return response.data;
  }

  async createUser(userData: { email: string; password: string; is_admin?: boolean }): Promise<User> {
    const response = await this.client.post<User>('/admin/users', userData);
    return response.data;
  }

  async updateUser(userId: string, userData: Partial<{ email: string; is_admin: boolean; is_active: boolean }>): Promise<User> {
    const response = await this.client.patch<User>(`/admin/users/${userId}`, userData);
    return response.data;
  }

  async deleteUser(userId: string): Promise<{ message: string }> {
    const response = await this.client.delete(`/admin/users/${userId}`);
    return response.data;
  }

  async getPrompts(): Promise<AgentPrompt[]> {
    const response = await this.client.get<AgentPrompt[]>('/admin/prompts');
    return response.data;
  }

  async getPrompt(promptKey: string): Promise<AgentPrompt> {
    const response = await this.client.get<AgentPrompt>(`/admin/prompts/${promptKey}`);
    return response.data;
  }

  async updatePrompt(
    promptKey: string,
    update: PromptUpdate
  ): Promise<{ message: string }> {
    const response = await this.client.put(`/admin/prompts/${promptKey}`, update);
    return response.data;
  }

  // ========== PUBLIC HEALTH CHECK ==========

  async healthCheck(): Promise<{ status: string; version: string; timestamp: number }> {
    const response = await this.client.get('/health');
    return response.data;
  }

  // ========== HELPERS ==========

  private getOrCreateDeviceId(): string {
    let deviceId = localStorage.getItem('device_id');
    if (!deviceId) {
      deviceId = `webchat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('device_id', deviceId);
    }
    return deviceId;
  }
}

// Export singleton instance
const apiClient = new ApiClient();
export default apiClient;
